/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import { applyFilters } from '@wordpress/hooks';
import {
	addCard,
	addSubmenu,
	alignCenter,
	alignJustify,
	alignLeft,
	alignNone,
	alignRight,
	archive,
	arrowDown,
	arrowLeft,
	arrowRight,
	arrowUp,
	atSymbol,
	aspectRatio,
	audio,
	backup,
	blockDefault,
	blockTable,
	border,
	box,
	brush,
	bug,
	button,
	buttons,
	calendar,
	cancelCircleFilled,
	caption,
	capturePhoto,
	captureVideo,
	category,
	chartBar,
	check,
	chevronDown,
	chevronLeft,
	chevronRight,
	chevronRightSmall,
	chevronUp,
	chevronUpDown,
	classic,
	close,
	closeSmall,
	cloudUpload,
	cloud,
	code,
	cog,
	color,
	column,
	columns,
	comment,
	commentAuthorAvatar,
	commentAuthorName,
	commentContent,
	commentEditLink,
	commentReplyLink,
	copy,
	cover,
	create,
	crop,
	currencyDollar,
	currencyEuro,
	currencyPound,
	customPostType,
	desktop,
	dragHandle,
	drawerLeft,
	drawerRight,
	download,
	edit,
	external,
	file,
	filter,
	flipHorizontal,
	flipVertical,
	formatBold,
	formatCapitalize,
	formatIndent,
	formatIndentRTL,
	formatItalic,
	formatListBullets,
	formatListBulletsRTL,
	formatListNumbered,
	formatListNumberedRTL,
	formatLtr,
	formatLowercase,
	formatOutdent,
	formatOutdentRTL,
	formatRtl,
	formatStrikethrough,
	formatUnderline,
	formatUppercase,
	fullscreen,
	gallery,
	globe,
	grid,
	group,
	handle,
	heading,
	help,
	helpFilled,
	inbox,
	institution,
	home,
	html,
	image,
	info,
	insertAfter,
	insertBefore,
	justifyLeft,
	justifyCenter,
	justifyRight,
	justifySpaceBetween,
	justifyStretch,
	key,
	keyboardClose,
	keyboardReturn,
	layout,
	lifesaver,
	lineDashed,
	lineDotted,
	lineSolid,
	link,
	linkOff,
	list,
	listItem,
	listView,
	lock,
	lockOutline,
	lockSmall,
	login,
	loop,
	mapMarker,
	media,
	mediaAndText,
	megaphone,
	menu,
	mobile,
	more,
	moreHorizontal,
	moreHorizontalMobile,
	moreVertical,
	moveTo,
	navigation,
	overlayText,
	pageBreak,
	customLink,
	page,
	pages,
	paragraph,
	payment,
	percent,
	positionCenter,
	positionLeft,
	positionRight,
	pencil,
	people,
	pin,
	plugins,
	plusCircleFilled,
	plusCircle,
	plus,
	post,
	postAuthor,
	postCategories,
	postContent,
	postComments,
	postCommentsCount,
	postCommentsForm,
	postDate,
	postExcerpt,
	postFeaturedImage,
	postList,
	postTerms,
	previous,
	next,
	preformatted,
	pullLeft,
	pullRight,
	pullquote,
	queryPagination,
	queryPaginationNext,
	queryPaginationNumbers,
	queryPaginationPrevious,
	quote,
	receipt,
	redo,
	removeBug,
	removeSubmenu,
	replace,
	reset,
	resizeCornerNE,
	reusableBlock,
	rotateLeft,
	rotateRight,
	row,
	rss,
	search,
	seen,
	separator,
	settings,
	shadow,
	share,
	shield,
	shortcode,
	shuffle,
	siteLogo,
	stack,
	starEmpty,
	starFilled,
	starHalf,
	store,
	stretchFullWidth,
	styles,
	shipping,
	stretchWide,
	subscript,
	superscript,
	swatch,
	symbol,
	symbolFilled,
	tableColumnAfter,
	tableColumnBefore,
	tableColumnDelete,
	tableRowAfter,
	tableRowBefore,
	tableRowDelete,
	table,
	tag,
	termDescription,
	footer,
	header,
	sidebar,
	textColor,
	tablet,
	tip,
	title,
	tool,
	trash,
	trendingDown,
	trendingUp,
	typography,
	undo,
	ungroup,
	unlock,
	unseen,
	update,
	upload,
	verse,
	video,
	warning,
	widget,
} from '@wordpress/icons';

/**
 * Internal dependencies
 */
import {
	amazon,
	bandcamp,
	behance,
	chain,
	codepen,
	deviantart,
	dribbble,
	dropbox,
	etsy,
	facebook,
	feed,
	fivehundredpx,
	flickr,
	foursquare,
	goodreads,
	google,
	github,
	instagram,
	lastfm,
	linkedin,
	mail,
	mastodon,
	meetup,
	medium,
	patreon,
	pinterest,
	pocket,
	redditSolid,
	reddit,
	skype,
	snapchat,
	soundcloud,
	spotify,
	telegram,
	threads,
	tiktok,
	tumblr,
	twitch,
	twitter,
	vimeo,
	vk,
	whatsapp,
	wordpress,
	x,
	yelp,
	youtube,
} from './wordpress/social';

// Temporary icon specifications that are not yet in the NPM package.
import { sparkles } from './wordpress/temp';

const wordpressSocialIcons = [
	{
		isDefault: true,
		name: 'wordpress',
		title: 'WordPress',
		icon: wordpress,
		categories: [ 'logos' ],
	},
	{
		name: 'fivehundredpx',
		title: '500px',
		icon: fivehundredpx,
		categories: [ 'logos' ],
	},
	{
		name: 'amazon',
		title: 'Amazon',
		icon: amazon,
		categories: [ 'logos' ],
	},
	{
		name: 'bandcamp',
		title: 'Bandcamp',
		icon: bandcamp,
		categories: [ 'logos' ],
	},
	{
		name: 'behance',
		title: 'Behance',
		icon: behance,
		categories: [ 'logos' ],
	},
	{
		name: 'chain',
		title: 'Link',
		icon: chain,
	},
	{
		name: 'codepen',
		title: 'CodePen',
		icon: codepen,
		categories: [ 'logos' ],
	},
	{
		name: 'deviantart',
		title: 'DeviantArt',
		icon: deviantart,
		categories: [ 'logos' ],
	},
	{
		name: 'dribbble',
		title: 'Dribbble',
		icon: dribbble,
		categories: [ 'logos' ],
	},
	{
		name: 'dropbox',
		title: 'Dropbox',
		icon: dropbox,
		categories: [ 'logos' ],
	},
	{
		name: 'etsy',
		title: 'Etsy',
		icon: etsy,
		categories: [ 'logos' ],
	},
	{
		name: 'facebook',
		title: 'Facebook',
		icon: facebook,
		categories: [ 'logos' ],
	},
	{
		name: 'feed',
		title: 'RSS Feed',
		icon: feed,
		categories: [ 'logos' ],
	},
	{
		name: 'flickr',
		title: 'Flickr',
		icon: flickr,
		categories: [ 'logos' ],
	},
	{
		name: 'foursquare',
		title: 'Foursquare',
		icon: foursquare,
		categories: [ 'logos' ],
	},
	{
		name: 'goodreads',
		title: 'Goodreads',
		icon: goodreads,
		categories: [ 'logos' ],
	},
	{
		name: 'google',
		title: 'Google',
		icon: google,
		categories: [ 'logos' ],
	},
	{
		name: 'github',
		title: 'GitHub',
		icon: github,
		categories: [ 'logos' ],
	},
	{
		name: 'instagram',
		title: 'Instagram',
		icon: instagram,
		categories: [ 'logos' ],
	},
	{
		name: 'lastfm',
		title: 'Last.fm',
		icon: lastfm,
		categories: [ 'logos' ],
	},
	{
		name: 'linkedin',
		title: 'LinkedIn',
		icon: linkedin,
		categories: [ 'logos' ],
	},
	{
		name: 'mail',
		title: 'Mail',
		keywords: [ 'email', 'e-mail' ],
		icon: mail,
	},
	{
		name: 'mastodon',
		title: 'Mastodon',
		icon: mastodon,
		categories: [ 'logos' ],
	},
	{
		name: 'meetup',
		title: 'Meetup',
		icon: meetup,
		categories: [ 'logos' ],
	},
	{
		name: 'medium',
		title: 'Medium',
		icon: medium,
		categories: [ 'logos' ],
	},
	{
		name: 'patreon',
		title: 'Patreon',
		icon: patreon,
		categories: [ 'logos' ],
	},
	{
		name: 'pinterest',
		title: 'Pinterest',
		icon: pinterest,
		categories: [ 'logos' ],
	},
	{
		name: 'pocket',
		title: 'Pocket',
		icon: pocket,
		categories: [ 'logos' ],
	},
	{
		name: 'reddit-solid',
		title: 'Reddit Solid',
		icon: redditSolid,
		categories: [ 'logos' ],
	},
	{
		name: 'reddit',
		title: 'Reddit',
		icon: reddit,
		categories: [ 'logos' ],
	},
	{
		name: 'skype',
		title: 'Skype',
		icon: skype,
		categories: [ 'logos' ],
	},
	{
		name: 'snapchat',
		title: 'Snapchat',
		icon: snapchat,
		categories: [ 'logos' ],
	},
	{
		name: 'soundcloud',
		title: 'SoundCloud',
		icon: soundcloud,
		categories: [ 'logos' ],
	},
	{
		name: 'spotify',
		title: 'Spotify',
		icon: spotify,
		categories: [ 'logos' ],
	},
	{
		name: 'telegram',
		title: 'Telegram',
		icon: telegram,
		categories: [ 'logos' ],
	},
	{
		name: 'threads',
		title: 'Threads',
		icon: threads,
		categories: [ 'logos' ],
	},
	{
		name: 'tiktok',
		title: 'TikTok',
		icon: tiktok,
		categories: [ 'logos' ],
	},
	{
		name: 'tumblr',
		title: 'Tumblr',
		icon: tumblr,
		categories: [ 'logos' ],
	},
	{
		name: 'twitch',
		title: 'Twitch',
		icon: twitch,
		categories: [ 'logos' ],
	},
	{
		isDefault: true,
		name: 'twitter',
		title: 'Twitter',
		icon: twitter,
		categories: [ 'logos' ],
	},
	{
		name: 'vimeo',
		title: 'Vimeo',
		icon: vimeo,
		categories: [ 'logos' ],
	},
	{
		name: 'vk',
		title: 'VK',
		icon: vk,
		categories: [ 'logos' ],
	},
	{
		name: 'whatsapp',
		title: 'WhatsApp',
		icon: whatsapp,
		categories: [ 'logos' ],
	},
	{
		name: 'x',
		title: 'X',
		icon: x,
		categories: [ 'logos' ],
		keywords: [ 'twitter' ],
	},
	{
		name: 'yelp',
		title: 'Yelp',
		icon: yelp,
		categories: [ 'logos' ],
	},
	{
		name: 'youtube',
		title: 'YouTube',
		icon: youtube,
		categories: [ 'logos' ],
	},
];

const wordpressIcons = [
	{
		name: 'addCard',
		title: __( 'Add Card', 'icon-block' ),
		icon: addCard,
	},
	{
		name: 'addSubmenu',
		title: __( 'Add Submenu', 'icon-block' ),
		icon: addSubmenu,
		categories: [ 'arrows' ],
	},
	{
		name: 'alignCenter',
		title: __( 'Align Center', 'icon-block' ),
		icon: alignCenter,
		categories: [ 'editor' ],
	},
	{
		name: 'alignJustify',
		title: __( 'Align Justify', 'icon-block' ),
		icon: alignJustify,
		categories: [ 'editor' ],
	},
	{
		name: 'alignLeft',
		title: __( 'Align Left', 'icon-block' ),
		icon: alignLeft,
		categories: [ 'editor' ],
	},
	{
		name: 'alignNone',
		title: __( 'Align None', 'icon-block' ),
		icon: alignNone,
		categories: [ 'editor' ],
	},
	{
		name: 'alignRight',
		title: __( 'Align Right', 'icon-block' ),
		icon: alignRight,
		categories: [ 'editor' ],
	},
	{
		name: 'archive',
		title: __( 'Archive', 'icon-block' ),
		icon: archive,
		categories: [ 'blocks' ],
	},
	{
		name: 'arrowDown',
		title: __( 'Arrow Down', 'icon-block' ),
		icon: arrowDown,
		categories: [ 'arrows' ],
	},
	{
		name: 'arrowLeft',
		title: __( 'Arrow Left', 'icon-block' ),
		icon: arrowLeft,
		categories: [ 'arrows' ],
	},
	{
		name: 'arrowRight',
		title: __( 'Arrow Right', 'icon-block' ),
		icon: arrowRight,
		categories: [ 'arrows' ],
	},
	{
		name: 'arrowUp',
		title: __( 'Arrow Up', 'icon-block' ),
		icon: arrowUp,
		categories: [ 'arrows' ],
	},
	{
		name: 'atSymbol',
		title: __( 'At Symbol', 'icon-block' ),
		icon: atSymbol,
	},
	{
		name: 'aspectRatio',
		title: __( 'Aspect Ratio', 'icon-block' ),
		icon: aspectRatio,
		categories: [ 'media' ],
	},
	{
		name: 'audio',
		title: __( 'Audio', 'icon-block' ),
		icon: audio,
		categories: [ 'media' ],
	},
	{
		name: 'backup',
		title: __( 'Backup', 'icon-block' ),
		icon: backup,
	},
	{
		name: 'blockDefault',
		title: __( 'Block Default', 'icon-block' ),
		icon: blockDefault,
		categories: [ 'blocks' ],
	},
	{
		name: 'blockTable',
		title: __( 'Block Table', 'icon-block' ),
		icon: blockTable,
	},
	{
		name: 'border',
		title: __( 'Border', 'icon-block' ),
		icon: border,
	},
	{
		name: 'box',
		title: __( 'Box', 'icon-block' ),
		icon: box,
	},
	{
		name: 'brush',
		title: __( 'Brush', 'icon-block' ),
		icon: brush,
	},
	{
		name: 'bug',
		title: __( 'Bug', 'icon-block' ),
		icon: bug,
	},
	{
		name: 'button',
		title: __( 'Button', 'icon-block' ),
		icon: button,
		categories: [ 'blocks' ],
	},
	{
		name: 'buttons',
		title: __( 'Buttons', 'icon-block' ),
		icon: buttons,
		categories: [ 'blocks' ],
	},
	{
		name: 'calendar',
		title: __( 'Calendar', 'icon-block' ),
		icon: calendar,
		categories: [ 'blocks' ],
	},
	{
		name: 'cancelCircleFilled',
		title: __( 'Cancel - Circle Filled', 'icon-block' ),
		icon: cancelCircleFilled,
	},
	{
		name: 'caption',
		title: __( 'Caption', 'icon-block' ),
		icon: caption,
	},
	{
		name: 'capturePhoto',
		title: __( 'Capture Photo', 'icon-block' ),
		icon: capturePhoto,
		categories: [ 'media' ],
	},
	{
		name: 'captureVideo',
		title: __( 'Capture Video', 'icon-block' ),
		icon: captureVideo,
		categories: [ 'media' ],
	},
	{
		name: 'category',
		title: __( 'Category', 'icon-block' ),
		icon: category,
		categories: [ 'blocks' ],
	},
	{
		name: 'chartBar',
		title: __( 'Chart Bar', 'icon-block' ),
		icon: chartBar,
	},
	{
		name: 'check',
		title: __( 'Check', 'icon-block' ),
		icon: check,
	},
	{
		name: 'chevronDown',
		title: __( 'Chevron Down', 'icon-block' ),
		icon: chevronDown,
		categories: [ 'arrows' ],
	},
	{
		name: 'chevronLeft',
		title: __( 'Chevron Left', 'icon-block' ),
		icon: chevronLeft,
		categories: [ 'arrows' ],
	},
	{
		name: 'chevronRight',
		title: __( 'Chevron Right', 'icon-block' ),
		icon: chevronRight,
		categories: [ 'arrows' ],
	},
	{
		name: 'chevronRightSmall',
		title: __( 'Chevron Right - Small', 'icon-block' ),
		icon: chevronRightSmall,
		categories: [ 'arrows' ],
	},
	{
		name: 'chevronUp',
		title: __( 'Chevron Up', 'icon-block' ),
		icon: chevronUp,
		categories: [ 'arrows' ],
	},
	{
		name: 'chevronUpDown',
		title: __( 'Chevron Up/Down', 'icon-block' ),
		icon: chevronUpDown,
		categories: [ 'arrows' ],
	},
	{
		name: 'classic',
		title: __( 'Classic', 'icon-block' ),
		icon: classic,
		categories: [ 'blocks' ],
	},
	{
		name: 'close',
		title: __( 'Close', 'icon-block' ),
		icon: close,
	},
	{
		name: 'closeSmall',
		title: __( 'Close - Small', 'icon-block' ),
		icon: closeSmall,
	},
	{
		name: 'cloudUpload',
		title: __( 'Cloud Upload', 'icon-block' ),
		icon: cloudUpload,
		categories: [ 'arrows' ],
	},
	{
		name: 'cloud',
		title: __( 'Cloud', 'icon-block' ),
		icon: cloud,
	},
	{
		name: 'code',
		title: __( 'Code', 'icon-block' ),
		icon: code,
		categories: [ 'blocks' ],
	},
	{
		name: 'cog',
		title: __( 'Cog', 'icon-block' ),
		icon: cog,
	},
	{
		name: 'color',
		title: __( 'Color', 'icon-block' ),
		icon: color,
	},
	{
		name: 'column',
		title: __( 'Column', 'icon-block' ),
		icon: column,
		categories: [ 'blocks' ],
	},
	{
		name: 'columns',
		title: __( 'Columns', 'icon-block' ),
		icon: columns,
		categories: [ 'blocks' ],
	},
	{
		name: 'comment',
		title: __( 'Comment', 'icon-block' ),
		icon: comment,
		categories: [ 'blocks' ],
	},
	{
		name: 'commentAuthorAvatar',
		title: __( 'Comment Author Avatar', 'icon-block' ),
		icon: commentAuthorAvatar,
		categories: [ 'blocks' ],
	},
	{
		name: 'commentAuthorName',
		title: __( 'Comment Author Name', 'icon-block' ),
		icon: commentAuthorName,
		categories: [ 'blocks' ],
	},
	{
		name: 'commentContent',
		title: __( 'Comment Content', 'icon-block' ),
		icon: commentContent,
		categories: [ 'blocks' ],
	},
	{
		name: 'commentEditLink',
		title: __( 'Comment Edit Link', 'icon-block' ),
		icon: commentEditLink,
		categories: [ 'blocks' ],
	},
	{
		name: 'commentReplyLink',
		title: __( 'Comment Reply Link', 'icon-block' ),
		icon: commentReplyLink,
		categories: [ 'blocks' ],
	},
	{
		name: 'copy',
		title: __( 'Copy', 'icon-block' ),
		icon: copy,
	},
	{
		name: 'cover',
		title: __( 'Cover', 'icon-block' ),
		icon: cover,
		categories: [ 'blocks' ],
	},
	{
		name: 'create',
		title: __( 'Create', 'icon-block' ),
		icon: create,
	},
	{
		name: 'crop',
		title: __( 'Crop', 'icon-block' ),
		icon: crop,
	},
	{
		name: 'currencyDollar',
		title: __( 'Currency Dollar', 'icon-block' ),
		icon: currencyDollar,
	},
	{
		name: 'currencyEuro',
		title: __( 'Currency Euro', 'icon-block' ),
		icon: currencyEuro,
	},
	{
		name: 'currencyPound',
		title: __( 'Currency Pound', 'icon-block' ),
		icon: currencyPound,
	},
	{
		name: 'customPostType',
		title: __( 'Custom Post Type', 'icon-block' ),
		icon: customPostType,
	},
	{
		name: 'desktop',
		title: __( 'Desktop', 'icon-block' ),
		icon: desktop,
		categories: [ 'devices' ],
	},
	{
		name: 'dragHandle',
		title: __( 'Drag Handle', 'icon-block' ),
		icon: dragHandle,
	},
	{
		name: 'drawerLeft',
		title: __( 'Drawer Left', 'icon-block' ),
		icon: drawerLeft,
		keywords: [ 'column' ],
	},
	{
		name: 'drawerRight',
		title: __( 'Drawer Right', 'icon-block' ),
		icon: drawerRight,
		keywords: [ 'column' ],
	},
	{
		name: 'download',
		title: __( 'Download', 'icon-block' ),
		icon: download,
		categories: [ 'arrows' ],
	},
	{
		name: 'edit',
		title: __( 'Edit', 'icon-block' ),
		icon: edit,
	},
	{
		name: 'external',
		title: __( 'External', 'icon-block' ),
		icon: external,
	},
	{
		name: 'file',
		title: __( 'File', 'icon-block' ),
		icon: file,
	},
	{
		name: 'filter',
		title: __( 'Filter', 'icon-block' ),
		icon: filter,
		keywords: [ 'triangle' ],
	},
	{
		name: 'flipHorizontal',
		title: __( 'Flip Horizontal', 'icon-block' ),
		icon: flipHorizontal,
	},
	{
		name: 'flipVertical',
		title: __( 'Flip Vertical', 'icon-block' ),
		icon: flipVertical,
	},
	{
		name: 'formatBold',
		title: __( 'Format Bold', 'icon-block' ),
		icon: formatBold,
		categories: [ 'editor' ],
	},
	{
		name: 'formatCapitalize',
		title: __( 'Format Capitalize', 'icon-block' ),
		icon: formatCapitalize,
		categories: [ 'editor' ],
	},
	{
		name: 'formatIndent',
		title: __( 'Format Indent', 'icon-block' ),
		icon: formatIndent,
		categories: [ 'editor' ],
	},
	{
		name: 'formatIndentRTL',
		title: __( 'Format Indent RTL', 'icon-block' ),
		icon: formatIndentRTL,
	},
	{
		name: 'formatItalic',
		title: __( 'Format Italic', 'icon-block' ),
		icon: formatItalic,
		categories: [ 'editor' ],
	},
	{
		name: 'formatListBullets',
		title: __( 'Format List Bullets', 'icon-block' ),
		icon: formatListBullets,
		categories: [ 'editor' ],
	},
	{
		name: 'formatListBulletsRTL',
		title: __( 'Format List Bullets RTL', 'icon-block' ),
		icon: formatListBulletsRTL,
		categories: [ 'editor' ],
	},
	{
		name: 'formatListNumbered',
		title: __( 'Format List Numbered', 'icon-block' ),
		icon: formatListNumbered,
		categories: [ 'editor' ],
	},
	{
		name: 'formatListNumberedRTL',
		title: __( 'Format List Numbered RTL', 'icon-block' ),
		icon: formatListNumberedRTL,
		categories: [ 'editor' ],
	},
	{
		name: 'formatLtr',
		title: __( 'Format LTR', 'icon-block' ),
		icon: formatLtr,
		categories: [ 'editor' ],
	},
	{
		name: 'formatLowercase',
		title: __( 'Format Lowercase', 'icon-block' ),
		icon: formatLowercase,
		categories: [ 'editor' ],
	},
	{
		name: 'formatOutdent',
		title: __( 'Format Outdent', 'icon-block' ),
		icon: formatOutdent,
		categories: [ 'editor' ],
	},
	{
		name: 'formatOutdentRTL',
		title: __( 'Format Outdent RTL', 'icon-block' ),
		icon: formatOutdentRTL,
		categories: [ 'editor' ],
	},
	{
		name: 'formatRtl',
		title: __( 'Format RTL', 'icon-block' ),
		icon: formatRtl,
		categories: [ 'editor' ],
	},
	{
		name: 'formatStrikethrough',
		title: __( 'Format Strikethrough', 'icon-block' ),
		icon: formatStrikethrough,
		categories: [ 'editor' ],
	},
	{
		name: 'formatUnderline',
		title: __( 'Format Underline', 'icon-block' ),
		icon: formatUnderline,
		categories: [ 'editor' ],
	},
	{
		name: 'formatUppercase',
		title: __( 'Format Uppercase', 'icon-block' ),
		icon: formatUppercase,
		categories: [ 'editor' ],
	},
	{
		name: 'fullscreen',
		title: __( 'Fullscreen', 'icon-block' ),
		icon: fullscreen,
		categories: [ 'editor' ],
	},
	{
		name: 'gallery',
		title: __( 'Gallery', 'icon-block' ),
		icon: gallery,
		categories: [ 'blocks', 'media' ],
	},
	{
		name: 'globe',
		title: __( 'Globe', 'icon-block' ),
		icon: globe,
	},
	{
		name: 'grid',
		title: __( 'Grid', 'icon-block' ),
		icon: grid,
	},
	{
		name: 'group',
		title: __( 'Group', 'icon-block' ),
		icon: group,
		categories: [ 'blocks' ],
	},
	{
		name: 'handle',
		title: __( 'Handle', 'icon-block' ),
		icon: handle,
	},
	{
		name: 'heading',
		title: __( 'Heading', 'icon-block' ),
		icon: heading,
		categories: [ 'blocks' ],
	},
	{
		name: 'help',
		title: __( 'Help', 'icon-block' ),
		icon: help,
	},
	{
		name: 'helpFilled',
		title: __( 'Help - Filled', 'icon-block' ),
		icon: helpFilled,
	},
	{
		name: 'inbox',
		title: __( 'Inbox', 'icon-block' ),
		icon: inbox,
	},
	{
		name: 'institution',
		title: __( 'Institution', 'icon-block' ),
		icon: institution,
	},
	{
		name: 'home',
		title: __( 'Home', 'icon-block' ),
		icon: home,
		categories: [ 'blocks' ],
	},
	{
		name: 'html',
		title: __( 'HTML', 'icon-block' ),
		icon: html,
		categories: [ 'blocks' ],
	},
	{
		isDefault: true,
		name: 'image',
		title: __( 'Image', 'icon-block' ),
		icon: image,
		categories: [ 'blocks', 'media' ],
	},
	{
		name: 'info',
		title: __( 'Info', 'icon-block' ),
		icon: info,
	},
	{
		name: 'insertAfter',
		title: __( 'Insert After', 'icon-block' ),
		icon: insertAfter,
		categories: [ 'editor' ],
	},
	{
		name: 'insertBefore',
		title: __( 'Insert Before', 'icon-block' ),
		icon: insertBefore,
		categories: [ 'editor' ],
	},
	{
		name: 'justifyLeft',
		title: __( 'Justify Left', 'icon-block' ),
		icon: justifyLeft,
		categories: [ 'editor' ],
	},
	{
		name: 'justifyCenter',
		title: __( 'Justify Center', 'icon-block' ),
		icon: justifyCenter,
		categories: [ 'editor' ],
	},
	{
		name: 'justifyRight',
		title: __( 'Justify Right', 'icon-block' ),
		icon: justifyRight,
		categories: [ 'editor' ],
	},
	{
		name: 'justifySpaceBetween',
		title: __( 'Justify Space Between', 'icon-block' ),
		icon: justifySpaceBetween,
		categories: [ 'editor' ],
	},
	{
		name: 'justifyStretch',
		title: __( 'Justify Stretch', 'icon-block' ),
		icon: justifyStretch,
		categories: [ 'editor' ],
	},
	{
		name: 'key',
		title: __( 'Key', 'icon-block' ),
		icon: key,
	},
	{
		name: 'keyboardClose',
		title: __( 'Keyboard Close', 'icon-block' ),
		icon: keyboardClose,
		categories: [ 'editor' ],
	},
	{
		name: 'keyboardReturn',
		title: __( 'Keyboard Return', 'icon-block' ),
		icon: keyboardReturn,
		categories: [ 'editor' ],
	},
	{
		name: 'layout',
		title: __( 'Layout', 'icon-block' ),
		icon: layout,
	},
	{
		name: 'lifesaver',
		title: __( 'Lifesaver', 'icon-block' ),
		icon: lifesaver,
	},
	{
		name: 'lineDashed',
		title: __( 'Line Dashed', 'icon-block' ),
		icon: lineDashed,
	},
	{
		name: 'lineDotted',
		title: __( 'Line Dotted', 'icon-block' ),
		icon: lineDotted,
	},
	{
		name: 'lineSolid',
		title: __( 'Line Solid', 'icon-block' ),
		icon: lineSolid,
	},
	{
		name: 'link',
		title: __( 'Link', 'icon-block' ),
		icon: link,
		categories: [ 'editor' ],
	},
	{
		name: 'linkOff',
		title: __( 'Link Off', 'icon-block' ),
		icon: linkOff,
		categories: [ 'editor' ],
	},
	{
		name: 'list',
		title: __( 'List', 'icon-block' ),
		icon: list,
		categories: [ 'blocks' ],
	},
	{
		name: 'listItem',
		title: __( 'List Item', 'icon-block' ),
		icon: listItem,
		categories: [ 'blocks' ],
	},
	{
		name: 'listView',
		title: __( 'List View', 'icon-block' ),
		icon: listView,
		categories: [ 'editor' ],
	},
	{
		name: 'lock',
		title: __( 'Lock', 'icon-block' ),
		icon: lock,
		categories: [ 'editor' ],
	},
	{
		name: 'lockOutline',
		title: __( 'Lock Outline', 'icon-block' ),
		icon: lockOutline,
		categories: [ 'editor' ],
	},
	{
		name: 'lockSmall',
		title: __( 'Lock Small', 'icon-block' ),
		icon: lockSmall,
		categories: [ 'editor' ],
	},
	{
		name: 'login',
		title: __( 'Login', 'icon-block' ),
		icon: login,
	},
	{
		name: 'loop',
		title: __( 'Loop', 'icon-block' ),
		icon: loop,
		categories: [ 'blocks' ],
	},
	{
		name: 'mapMarker',
		title: __( 'Map Marker', 'icon-block' ),
		icon: mapMarker,
	},
	{
		name: 'media',
		title: __( 'Media', 'icon-block' ),
		icon: media,
		categories: [ 'blocks', 'media' ],
	},
	{
		name: 'mediaAndText',
		title: __( 'Media & Text', 'icon-block' ),
		icon: mediaAndText,
		categories: [ 'blocks', 'media' ],
	},
	{
		name: 'megaphone',
		title: __( 'Megaphone', 'icon-block' ),
		icon: megaphone,
	},
	{
		name: 'menu',
		title: __( 'Menu', 'icon-block' ),
		icon: menu,
	},
	{
		name: 'mobile',
		title: __( 'Mobile', 'icon-block' ),
		icon: mobile,
		categories: [ 'devices' ],
	},
	{
		name: 'more',
		title: __( 'More', 'icon-block' ),
		icon: more,
	},
	{
		name: 'moreHorizontal',
		title: __( 'More Horizontal', 'icon-block' ),
		icon: moreHorizontal,
	},
	{
		name: 'moreHorizontalMobile',
		title: __( 'More Horizontal - Mobile', 'icon-block' ),
		icon: moreHorizontalMobile,
	},
	{
		name: 'moreVertical',
		title: __( 'More Vertical', 'icon-block' ),
		icon: moreVertical,
	},
	{
		name: 'moveTo',
		title: __( 'Move To', 'icon-block' ),
		icon: moveTo,
		categories: [ 'arrows' ],
	},
	{
		name: 'navigation',
		title: __( 'Navigation', 'icon-block' ),
		icon: navigation,
	},
	{
		name: 'overlayText',
		title: __( 'Overlay Text', 'icon-block' ),
		icon: overlayText,
	},
	{
		name: 'pageBreak',
		title: __( 'Page Break', 'icon-block' ),
		icon: pageBreak,
	},
	{
		name: 'customLink',
		title: __( 'Custom Link', 'icon-block' ),
		icon: customLink,
	},
	{
		name: 'page',
		title: __( 'Page', 'icon-block' ),
		icon: page,
	},
	{
		name: 'pages',
		title: __( 'Pages', 'icon-block' ),
		icon: pages,
	},
	{
		name: 'paragraph',
		title: __( 'Paragraph', 'icon-block' ),
		icon: paragraph,
	},
	{
		name: 'payment',
		title: __( 'Payment', 'icon-block' ),
		icon: payment,
	},
	{
		name: 'percent',
		title: __( 'Percent', 'icon-block' ),
		icon: percent,
	},
	{
		name: 'positionCenter',
		title: __( 'Position Center', 'icon-block' ),
		icon: positionCenter,
	},
	{
		name: 'positionLeft',
		title: __( 'Position Left', 'icon-block' ),
		icon: positionLeft,
	},
	{
		name: 'positionRight',
		title: __( 'Position Right', 'icon-block' ),
		icon: positionRight,
	},
	{
		name: 'post',
		title: __( 'Post', 'icon-block' ),
		icon: post,
	},
	{
		name: 'pencil',
		title: __( 'Pencil', 'icon-block' ),
		icon: pencil,
	},
	{
		name: 'people',
		title: __( 'People', 'icon-block' ),
		icon: people,
	},
	{
		name: 'pin',
		title: __( 'Pin', 'icon-block' ),
		icon: pin,
	},
	{
		name: 'plugins',
		title: __( 'Plugins', 'icon-block' ),
		icon: plugins,
	},
	{
		name: 'plusCircleFilled',
		title: __( 'Plus Circle - Filled', 'icon-block' ),
		icon: plusCircleFilled,
	},
	{
		name: 'plusCircle',
		title: __( 'Plus Circle', 'icon-block' ),
		icon: plusCircle,
	},
	{
		name: 'plus',
		title: __( 'Plus', 'icon-block' ),
		icon: plus,
	},
	{
		name: 'postAuthor',
		title: __( 'Post Author', 'icon-block' ),
		icon: postAuthor,
		categories: [ 'blocks' ],
	},
	{
		name: 'postCategories',
		title: __( 'Post Categories', 'icon-block' ),
		icon: postCategories,
		categories: [ 'blocks' ],
	},
	{
		name: 'postContent',
		title: __( 'Post Content', 'icon-block' ),
		icon: postContent,
		categories: [ 'blocks' ],
	},
	{
		name: 'postComments',
		title: __( 'Post Comments', 'icon-block' ),
		icon: postComments,
		categories: [ 'blocks' ],
	},
	{
		name: 'postCommentsCount',
		title: __( 'Post Comment Count', 'icon-block' ),
		icon: postCommentsCount,
	},
	{
		name: 'postCommentsForm',
		title: __( 'Post Comments Form', 'icon-block' ),
		icon: postCommentsForm,
		categories: [ 'blocks' ],
	},
	{
		name: 'postDate',
		title: __( 'Post Date', 'icon-block' ),
		icon: postDate,
		categories: [ 'blocks' ],
	},
	{
		name: 'postExcerpt',
		title: __( 'Post Excerpt', 'icon-block' ),
		icon: postExcerpt,
		categories: [ 'blocks' ],
	},
	{
		name: 'postFeaturedImage',
		title: __( 'Post Featured Image', 'icon-block' ),
		icon: postFeaturedImage,
		categories: [ 'blocks' ],
	},
	{
		name: 'postList',
		title: __( 'Post List', 'icon-block' ),
		icon: postList,
		categories: [ 'blocks' ],
	},
	{
		name: 'postTerms',
		title: __( 'Post Terms', 'icon-block' ),
		icon: postTerms,
		categories: [ 'blocks' ],
	},
	{
		name: 'previous',
		title: __( 'Previous', 'icon-block' ),
		icon: previous,
		categories: [ 'arrows' ],
	},
	{
		name: 'next',
		title: __( 'Next', 'icon-block' ),
		icon: next,
		categories: [ 'arrows' ],
	},
	{
		name: 'preformatted',
		title: __( 'Preformatted', 'icon-block' ),
		icon: preformatted,
	},
	{
		name: 'pullLeft',
		title: __( 'Pull Left', 'icon-block' ),
		icon: pullLeft,
	},
	{
		name: 'pullRight',
		title: __( 'Pull Right', 'icon-block' ),
		icon: pullRight,
	},
	{
		name: 'pullquote',
		title: __( 'Pull Quote', 'icon-block' ),
		icon: pullquote,
	},
	{
		name: 'queryPagination',
		title: __( 'Query Pagination', 'icon-block' ),
		icon: queryPagination,
	},
	{
		name: 'queryPaginationNext',
		title: __( 'Query Pagination Next', 'icon-block' ),
		icon: queryPaginationNext,
		categories: [ 'arrows' ],
	},
	{
		name: 'queryPaginationNumbers',
		title: __( 'Query Pagination Numbers', 'icon-block' ),
		icon: queryPaginationNumbers,
	},
	{
		name: 'queryPaginationPrevious',
		title: __( 'Query Pagination Previous', 'icon-block' ),
		icon: queryPaginationPrevious,
		categories: [ 'arrows' ],
	},
	{
		name: 'quote',
		title: __( 'Quote', 'icon-block' ),
		icon: quote,
		categories: [ 'blocks' ],
	},
	{
		name: 'receipt',
		title: __( 'Receipt', 'icon-block' ),
		icon: receipt,
	},
	{
		name: 'redo',
		title: __( 'Redo', 'icon-block' ),
		icon: redo,
		categories: [ 'arrows', 'editor' ],
	},
	{
		name: 'removeBug',
		title: __( 'Remove Bug', 'icon-block' ),
		icon: removeBug,
	},
	{
		name: 'removeSubmenu',
		title: __( 'Remove Submenu', 'icon-block' ),
		icon: removeSubmenu,
	},
	{
		name: 'replace',
		title: __( 'Replace', 'icon-block' ),
		icon: replace,
		categories: [ 'arrows' ],
	},
	{
		name: 'reset',
		title: __( 'Reset', 'icon-block' ),
		icon: reset,
		keywords: [ 'minus' ],
	},
	{
		name: 'resizeCornerNE',
		title: __( 'Resize Corner', 'icon-block' ),
		icon: resizeCornerNE,
		categories: [ 'arrows' ],
	},
	{
		name: 'reusableBlock',
		title: __( 'Reusable Block', 'icon-block' ),
		icon: reusableBlock,
		categories: [ 'arrows' ],
	},
	{
		name: 'rotateLeft',
		title: __( 'Rotate Left', 'icon-block' ),
		icon: rotateLeft,
		categories: [ 'arrows' ],
	},
	{
		name: 'rotateRight',
		title: __( 'Rotate Right', 'icon-block' ),
		icon: rotateRight,
		categories: [ 'arrows' ],
	},
	{
		name: 'row',
		title: __( 'Row', 'icon-block' ),
		icon: row,
	},
	{
		name: 'rss',
		title: __( 'RSS', 'icon-block' ),
		icon: rss,
	},
	{
		name: 'search',
		title: __( 'Search', 'icon-block' ),
		icon: search,
		keywords: [ 'magnifying glass' ],
	},
	{
		name: 'seen',
		title: __( 'Seen', 'icon-block' ),
		icon: seen,
		keywords: [ 'eye', 'visible' ],
	},
	{
		name: 'separator',
		title: __( 'Separator', 'icon-block' ),
		icon: separator,
		categories: [ 'blocks' ],
	},
	{
		name: 'settings',
		title: __( 'Settings', 'icon-block' ),
		icon: settings,
	},
	{
		name: 'shadow',
		title: __( 'Shadow', 'icon-block' ),
		icon: shadow,
		keywords: [ 'sun' ],
	},
	{
		name: 'share',
		title: __( 'Share', 'icon-block' ),
		icon: share,
	},
	{
		name: 'shield',
		title: __( 'Shield', 'icon-block' ),
		icon: shield,
	},
	{
		name: 'shortcode',
		title: __( 'Shortcode', 'icon-block' ),
		icon: shortcode,
		categories: [ 'blocks' ],
	},
	{
		name: 'shuffle',
		title: __( 'Shuffle', 'icon-block' ),
		icon: shuffle,
	},
	{
		name: 'siteLogo',
		title: __( 'Site Logo', 'icon-block' ),
		icon: siteLogo,
		categories: [ 'blocks', 'media' ],
	},
	{
		isDefault: true,
		name: 'sparkles',
		title: __( 'Sparkles', 'icon-block' ),
		icon: sparkles,
	},
	{
		name: 'stack',
		title: __( 'Stack', 'icon-block' ),
		icon: stack,
	},
	{
		name: 'starEmpty',
		title: __( 'Star Empty', 'icon-block' ),
		icon: starEmpty,
	},
	{
		name: 'starFilled',
		title: __( 'Star Filled', 'icon-block' ),
		icon: starFilled,
	},
	{
		name: 'starHalf',
		title: __( 'Star Half', 'icon-block' ),
		icon: starHalf,
	},
	{
		name: 'store',
		title: __( 'Store', 'icon-block' ),
		icon: store,
	},
	{
		name: 'stretchFullWidth',
		title: __( 'Stretch Full Width', 'icon-block' ),
		icon: stretchFullWidth,
		categories: [ 'editor' ],
	},
	{
		name: 'styles',
		title: __( 'Styles', 'icon-block' ),
		icon: styles,
	},
	{
		isDefault: true,
		name: 'shipping',
		title: __( 'Shipping', 'icon-block' ),
		icon: shipping,
		keywords: [ 'truck' ],
	},
	{
		name: 'stretchWide',
		title: __( 'Stretch Wide', 'icon-block' ),
		icon: stretchWide,
		categories: [ 'editor' ],
	},
	{
		name: 'subscript',
		title: __( 'Subscript', 'icon-block' ),
		icon: subscript,
		categories: [ 'editor' ],
	},
	{
		name: 'superscript',
		title: __( 'Superscript', 'icon-block' ),
		icon: superscript,
		categories: [ 'editor' ],
	},
	{
		name: 'swatch',
		title: __( 'Swatch', 'icon-block' ),
		icon: swatch,
	},
	{
		name: 'symbol',
		title: __( 'Symbol', 'icon-block' ),
		icon: symbol,
	},
	{
		name: 'symbolFilled',
		title: __( 'Symbol - Filled', 'icon-block' ),
		icon: symbolFilled,
	},
	{
		name: 'tableColumnAfter',
		title: __( 'Table Column After', 'icon-block' ),
		icon: tableColumnAfter,
		categories: [ 'editor' ],
	},
	{
		name: 'tableColumnBefore',
		title: __( 'Table Column Before', 'icon-block' ),
		icon: tableColumnBefore,
		categories: [ 'editor' ],
	},
	{
		name: 'tableColumnDelete',
		title: __( 'Table Column Delete', 'icon-block' ),
		icon: tableColumnDelete,
		categories: [ 'editor' ],
	},
	{
		name: 'tableRowAfter',
		title: __( 'Table Row After', 'icon-block' ),
		icon: tableRowAfter,
		categories: [ 'editor' ],
	},
	{
		name: 'tableRowBefore',
		title: __( 'Table Row Before', 'icon-block' ),
		icon: tableRowBefore,
		categories: [ 'editor' ],
	},
	{
		name: 'tableRowDelete',
		title: __( 'Table Row Delete', 'icon-block' ),
		icon: tableRowDelete,
		categories: [ 'editor' ],
	},
	{
		name: 'table',
		title: __( 'Table', 'icon-block' ),
		icon: table,
	},
	{
		name: 'tag',
		title: __( 'Tag', 'icon-block' ),
		icon: tag,
	},
	{
		name: 'termDescription',
		title: __( 'Term Description', 'icon-block' ),
		icon: termDescription,
		categories: [ 'blocks' ],
	},
	{
		name: 'footer',
		title: __( 'Footer', 'icon-block' ),
		icon: footer,
	},
	{
		name: 'header',
		title: __( 'Header', 'icon-block' ),
		icon: header,
	},
	{
		name: 'sidebar',
		title: __( 'Sidebar', 'icon-block' ),
		icon: sidebar,
	},
	{
		name: 'textColor',
		title: __( 'Text Color', 'icon-block' ),
		icon: textColor,
	},
	{
		name: 'tablet',
		title: __( 'Tablet', 'icon-block' ),
		icon: tablet,
		categories: [ 'devices' ],
	},
	{
		name: 'title',
		title: __( 'Title', 'icon-block' ),
		icon: title,
	},
	{
		name: 'tip',
		title: __( 'Tip', 'icon-block' ),
		icon: tip,
	},
	{
		name: 'tool',
		title: __( 'Tool', 'icon-block' ),
		icon: tool,
	},
	{
		name: 'trash',
		title: __( 'Trash', 'icon-block' ),
		icon: trash,
	},
	{
		name: 'trendingDown',
		title: __( 'Trending Down', 'icon-block' ),
		icon: trendingDown,
		categories: [ 'arrows' ],
	},
	{
		name: 'trendingUp',
		title: __( 'Trending Up', 'icon-block' ),
		icon: trendingUp,
		categories: [ 'arrows' ],
	},
	{
		name: 'typography',
		title: __( 'Typography', 'icon-block' ),
		icon: typography,
	},
	{
		name: 'undo',
		title: __( 'Undo', 'icon-block' ),
		icon: undo,
		categories: [ 'arrows', 'editor' ],
	},
	{
		name: 'ungroup',
		title: __( 'Ungroup', 'icon-block' ),
		icon: ungroup,
		categories: [ 'editor' ],
	},
	{
		name: 'unlock',
		title: __( 'Unlock', 'icon-block' ),
		icon: unlock,
		categories: [ 'editor' ],
	},
	{
		name: 'unseen',
		title: __( 'Unseen', 'icon-block' ),
		icon: unseen,
		keywords: [ 'eye', 'hidden' ],
	},
	{
		name: 'update',
		title: __( 'Update', 'icon-block' ),
		icon: update,
	},
	{
		name: 'upload',
		title: __( 'Upload', 'icon-block' ),
		icon: upload,
		categories: [ 'arrows' ],
	},
	{
		isDefault: true,
		name: 'verse',
		title: __( 'Verse', 'icon-block' ),
		icon: verse,
		categories: [ 'blocks' ],
	},
	{
		name: 'video',
		title: __( 'Video', 'icon-block' ),
		icon: video,
		categories: [ 'media' ],
	},
	{
		name: 'warning',
		title: __( 'Warning', 'icon-block' ),
		icon: warning,
	},
	{
		name: 'widget',
		title: __( 'Widget', 'icon-block' ),
		icon: widget,
	},
];

const newlyAdded = [
	{
		name: 'academic-cap',
		title: __( 'Academic Cap', 'icon-block' ),
		icon:<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
		<path stroke-linecap="round" stroke-linejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5" />
	  </svg>,
		categories: [ 'newlyAdded' ],
	},
	{
		name: 'archive-box-arrow-down',
		title: __( 'Archive Box Arrow Down', 'icon-block' ),
		icon:<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
		<path stroke-linecap="round" stroke-linejoin="round" d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0-3-3m3 3 3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
	  </svg>,
		categories: [ 'newlyAdded' ],
	},
	{
		name: 'bell',
		title: __( 'Bell', 'icon-block' ),
		icon:<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
		<path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
	  </svg>,
		categories: [ 'newlyAdded' ],
	},
	{
		name: 'bolc',
		title: __( 'Bold', 'icon-block' ),
		icon:<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
		<path stroke-linejoin="round" d="M6.75 3.744h-.753v8.25h7.125a4.125 4.125 0 0 0 0-8.25H6.75Zm0 0v.38m0 16.122h6.747a4.5 4.5 0 0 0 0-9.001h-7.5v9h.753Zm0 0v-.37m0-15.751h6a3.75 3.75 0 1 1 0 7.5h-6m0-7.5v7.5m0 0v8.25m0-8.25h6.375a4.125 4.125 0 0 1 0 8.25H6.75m.747-15.38h4.875a3.375 3.375 0 0 1 0 6.75H7.497v-6.75Zm0 7.5h5.25a3.75 3.75 0 0 1 0 7.5h-5.25v-7.5Z" />
	  </svg>,
		categories: [ 'newlyAdded' ],
	},
	{
		name: 'bolt',
		title: __( 'Bolt', 'icon-block' ),
		icon:<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
		<path stroke-linecap="round" stroke-linejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
	  </svg>
	  ,
		categories: [ 'newlyAdded' ],
	},
	{
		name: 'bug-ant',
		title: __( 'Bug Ant', 'icon-block' ),
		icon:<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
		<path stroke-linecap="round" stroke-linejoin="round" d="M12 12.75c1.148 0 2.278.08 3.383.237 1.037.146 1.866.966 1.866 2.013 0 3.728-2.35 6.75-5.25 6.75S6.75 18.728 6.75 15c0-1.046.83-1.867 1.866-2.013A24.204 24.204 0 0 1 12 12.75Zm0 0c2.883 0 5.647.508 8.207 1.44a23.91 23.91 0 0 1-1.152 6.06M12 12.75c-2.883 0-5.647.508-8.208 1.44.125 2.104.52 4.136 1.153 6.06M12 12.75a2.25 2.25 0 0 0 2.248-2.354M12 12.75a2.25 2.25 0 0 1-2.248-2.354M12 8.25c.995 0 1.971-.08 2.922-.236.403-.066.74-.358.795-.762a3.778 3.778 0 0 0-.399-2.25M12 8.25c-.995 0-1.97-.08-2.922-.236-.402-.066-.74-.358-.795-.762a3.734 3.734 0 0 1 .4-2.253M12 8.25a2.25 2.25 0 0 0-2.248 2.146M12 8.25a2.25 2.25 0 0 1 2.248 2.146M8.683 5a6.032 6.032 0 0 1-1.155-1.002c.07-.63.27-1.222.574-1.747m.581 2.749A3.75 3.75 0 0 1 15.318 5m0 0c.427-.283.815-.62 1.155-.999a4.471 4.471 0 0 0-.575-1.752M4.921 6a24.048 24.048 0 0 0-.392 3.314c1.668.546 3.416.914 5.223 1.082M19.08 6c.205 1.08.337 2.187.392 3.314a23.882 23.882 0 0 1-5.223 1.082" />
	  </svg>
	  ,
		categories: [ 'newlyAdded' ],
	},
	{
		name: 'chart-bar-square',
		title: __( 'Chart Bar Square', 'icon-block' ),
		icon:<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
		<path stroke-linecap="round" stroke-linejoin="round" d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z" />
	  </svg>,
		categories: [ 'newlyAdded' ],
	},
	{
		name: 'chart-pie',
		title: __( 'Chart Pie', 'icon-block' ),
		icon:<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
		<path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6a7.5 7.5 0 1 0 7.5 7.5h-7.5V6Z" />
		<path stroke-linecap="round" stroke-linejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0 0 13.5 3v7.5Z" />
	  </svg>
	  ,
		categories: [ 'newlyAdded' ],
	},

	{
		name: 'check-badge',
		title: __( 'Check Badge', 'icon-block' ),
		icon:<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
		<path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" />
	  </svg>
	  
	  ,
		categories: [ 'newlyAdded' ],
	},
	{
		name: 'clock',
		title: __( 'Clock', 'icon-block' ),
		icon:<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
		<path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
	  </svg>,
		categories: [ 'newlyAdded' ],
	},
	{
		name: 'gift-top',
		title: __( 'Gift Top', 'icon-block' ),
		icon:<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
		<path stroke-linecap="round" stroke-linejoin="round" d="M12 3.75v16.5M2.25 12h19.5M6.375 17.25a4.875 4.875 0 0 0 4.875-4.875V12m6.375 5.25a4.875 4.875 0 0 1-4.875-4.875V12m-9 8.25h16.5a1.5 1.5 0 0 0 1.5-1.5V5.25a1.5 1.5 0 0 0-1.5-1.5H3.75a1.5 1.5 0 0 0-1.5 1.5v13.5a1.5 1.5 0 0 0 1.5 1.5Zm12.621-9.44c-1.409 1.41-4.242 1.061-4.242 1.061s-.349-2.833 1.06-4.242a2.25 2.25 0 0 1 3.182 3.182ZM10.773 7.63c1.409 1.409 1.06 4.242 1.06 4.242S9 12.22 7.592 10.811a2.25 2.25 0 1 1 3.182-3.182Z" />
	  </svg>
	  
	  ,
		categories: [ 'newlyAdded' ],
	},
	{
		name: 'hand-raised',
		title: __( 'Hand Raised', 'icon-block' ),
		icon:<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
		<path stroke-linecap="round" stroke-linejoin="round" d="M10.05 4.575a1.575 1.575 0 1 0-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 0 1 3.15 0v1.5m-3.15 0 .075 5.925m3.075.75V4.575m0 0a1.575 1.575 0 0 1 3.15 0V15M6.9 7.575a1.575 1.575 0 1 0-3.15 0v8.175a6.75 6.75 0 0 0 6.75 6.75h2.018a5.25 5.25 0 0 0 3.712-1.538l1.732-1.732a5.25 5.25 0 0 0 1.538-3.712l.003-2.024a.668.668 0 0 1 .198-.471 1.575 1.575 0 1 0-2.228-2.228 3.818 3.818 0 0 0-1.12 2.687M6.9 7.575V12m6.27 4.318A4.49 4.49 0 0 1 16.35 15m.002 0h-.002" />
	  </svg>
	  ,
		categories: [ 'newlyAdded' ],
	},
	{
		name: 'hand-thumb-down',
		title: __( 'Thumbs Down', 'icon-block' ),
		icon:<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
		<path stroke-linecap="round" stroke-linejoin="round" d="M7.498 15.25H4.372c-1.026 0-1.945-.694-2.054-1.715a12.137 12.137 0 0 1-.068-1.285c0-2.848.992-5.464 2.649-7.521C5.287 4.247 5.886 4 6.504 4h4.016a4.5 4.5 0 0 1 1.423.23l3.114 1.04a4.5 4.5 0 0 0 1.423.23h1.294M7.498 15.25c.618 0 .991.724.725 1.282A7.471 7.471 0 0 0 7.5 19.75 2.25 2.25 0 0 0 9.75 22a.75.75 0 0 0 .75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 0 0 2.86-2.4c.498-.634 1.226-1.08 2.032-1.08h.384m-10.253 1.5H9.7m8.075-9.75c.01.05.027.1.05.148.593 1.2.925 2.55.925 3.977 0 1.487-.36 2.89-.999 4.125m.023-8.25c-.076-.365.183-.75.575-.75h.908c.889 0 1.713.518 1.972 1.368.339 1.11.521 2.287.521 3.507 0 1.553-.295 3.036-.831 4.398-.306.774-1.086 1.227-1.918 1.227h-1.053c-.472 0-.745-.556-.5-.96a8.95 8.95 0 0 0 .303-.54" />
	  </svg>
	  ,
		categories: [ 'newlyAdded' ],
	},
	{
		name: 'hand-thumb-up',
		title: __( 'Thumb Up', 'icon-block' ),
		icon:<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
		<path stroke-linecap="round" stroke-linejoin="round" d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m10.598-9.75H14.25M5.904 18.5c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 9.953 4.167 9.5 5 9.5h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z" />
	  </svg>
	  ,
		categories: [ 'newlyAdded' ],
	},
];

const newvideo = [
	{
		name: 'play',
		title: __( 'Play', 'icon-block' ),
		icon:<svg  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="5 3 19 12 5 21 5 3" />
        </svg>,
		categories: [ 'video' ],
	},

	{
		name: 'pause',
		title: __( 'Pause', 'icon-block' ),
		icon:<svg  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="6" y="4" width="4" height="16" />
            <rect x="14" y="4" width="4" height="16" />
        </svg>,
		categories: [ 'video' ],
	},

	{
		name: 'stop',
		title: __( 'Stop', 'icon-block' ),
		icon:<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 30" class="gkit-icon"><path d="M35.716,10.449a.932.932,0,0,0-1.289.3.945.945,0,0,0,.3,1.3,3.493,3.493,0,0,1,0,5.908L19.386,27.58a3.4,3.4,0,0,1-3.509.09A3.442,3.442,0,0,1,14.1,24.626V5.374A3.442,3.442,0,0,1,15.877,2.33a3.4,3.4,0,0,1,3.509.09l8.93,5.6a.932.932,0,0,0,1.289-.3.945.945,0,0,0-.3-1.3l-8.93-5.6A5.236,5.236,0,0,0,14.969.684a5.3,5.3,0,0,0-2.743,4.69V24.626a5.31,5.31,0,0,0,8.15,4.551l15.34-9.626a5.382,5.382,0,0,0,0-9.1Z" transform="translate(-12.226)"></path></svg>,
		categories: [ 'video' ],
	},

	{
		name: 'rewind',
		title: __( 'Rewind', 'icon-block' ),
		icon:<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" class="gkit-icon"><path d="M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80V432c0 17.4 9.4 33.4 24.5 41.9s33.7 8.1 48.5-.9L361 297c14.3-8.7 23-24.2 23-41s-8.7-32.2-23-41L73 39z"></path></svg>,
		categories: [ 'video' ],
	},

	{
		name: 'record',
		title: __( 'Record', 'icon-block' ),
		icon:<svg version="1.1" xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 32 32" class="gkit-icon"><title>play</title>
            <path d="M30.231 9.075c-1.119-2.175-2.756-4.094-4.725-5.55-0.288-0.212-0.7-0.15-0.913 0.138s-0.15 0.7 0.137 0.912c1.813 1.338 3.313 3.1 4.344 5.1 1.081 2.094 1.625 4.356 1.625 6.725 0 8.106-6.594 14.7-14.7 14.7s-14.7-6.594-14.7-14.7 6.594-14.7 14.7-14.7c0.363 0 0.65-0.294 0.65-0.65s-0.288-0.65-0.65-0.65c-8.825 0-16 7.175-16 16s7.175 16 16 16 16-7.175 16-16c0-2.544-0.613-5.075-1.769-7.325zM11.906 8.969c-0.206 0.119-0.331 0.331-0.331 0.569v14.294c0 0.363 0.294 0.65 0.65 0.65s0.65-0.294 0.65-0.65v-13.15l9.644 5.737-7.088 4.569c-0.3 0.194-0.387 0.6-0.194 0.9s0.6 0.387 0.9 0.194l7.975-5.137c0.188-0.125 0.3-0.331 0.3-0.556-0.006-0.225-0.125-0.431-0.319-0.55l-11.531-6.863c-0.206-0.119-0.45-0.119-0.656-0.006z"></path>
            </svg>,
		categories: [ 'video' ],
	},
];

const icons = [
	{
		isDefault: false,
		type: 'wordpress',
		title: __( 'WordPress', 'icon-block' ),
		icons: [].concat( wordpressSocialIcons, wordpressIcons,newlyAdded, newvideo),
		categories: [
			{
				name: 'arrows',
				title: __( 'Arrows', 'icon-block' ),
			},
			{
				name: 'blocks',
				title: __( 'Blocks', 'icon-block' ),
			},
			{
				name: 'devices',
				title: __( 'Devices', 'icon-block' ),
			},
			{
				name: 'editor',
				title: __( 'Editor', 'icon-block' ),
			},
			{
				name: 'logos',
				title: __( 'Logos', 'icon-block' ),
			},
			{
				name: 'media',
				title: __( 'Media', 'icon-block' ),
			},
			{
				name: 'video',
				title: __( 'Video', 'icon-block' ),
			},
			{
				name: 'newlyAdded',
				title: __( 'NewlyAdded', 'icon-block' ),
			},
		],
	},
];

// Allow third parties to add their own icon types via filter.
export default function getIcons() {
	return applyFilters( 'iconBlock.icons', icons );
}
